﻿/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView,
  StatusBar, TextInput, FlatList, ActivityIndicator, Alert, Modal,
  Dimensions, Animated, RefreshControl, Image, Switch, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfessional } from '../../context/ProfessionalContext';
import { useProfessionalOnboarding } from '../../context/ProfessionalOnboardingContext';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

// ═══════════════════════════════════════════════════════════════
// API CONFIG
// ═══════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const { width } = Dimensions.get('window');

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════
const SALARY_RANGES = [
  { id: 'all', label: 'Any Salary' },
  { id: '0-30000', label: 'Up to £30k', min: 0, max: 30000 },
  { id: '30000-50000', label: '£30k – £50k', min: 30000, max: 50000 },
  { id: '50000-70000', label: '£50k – £70k', min: 50000, max: 70000 },
  { id: '70000-100000', label: '£70k – £100k', min: 70000, max: 100000 },
  { id: '100000+', label: '£100k+', min: 100000, max: 999999 },
];

const EXPERIENCE_LEVELS = [
  { id: 'all', label: 'All Levels' },
  { id: 'entry', label: 'Entry Level' },
  { id: 'mid', label: 'Mid Level' },
  { id: 'senior', label: 'Senior' },
  { id: 'director', label: 'Director+' },
];

const SORT_OPTIONS = [
  { id: 'relevance', label: 'Best Match' },
  { id: 'date', label: 'Newest First' },
  { id: 'salary_desc', label: 'Salary: High → Low' },
  { id: 'salary_asc', label: 'Salary: Low → High' },
];

const JOB_CATEGORIES = [
  { id: 'all', title: 'All Jobs', icon: 'briefcase-outline', color: '#667eea', filter: null },
  { id: 'fulltime', title: 'Full Time', icon: 'time-outline', color: '#4facfe', filter: 'full-time' },
  { id: 'parttime', title: 'Part Time', icon: 'hourglass-outline', color: '#43e97b', filter: 'part-time' },
  { id: 'freelance', title: 'Freelance', icon: 'laptop-outline', color: '#fa709a', filter: 'freelance' },
  { id: 'entrylevel', title: 'Entry Level', icon: 'school-outline', color: '#ff6b6b', filter: 'entry' },
  { id: 'remote', title: 'Remote', icon: 'home-outline', color: '#4ecdc4', filter: 'remote' },
  { id: 'contract', title: 'Contract', icon: 'document-text-outline', color: '#f59e0b', filter: 'contract' },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function FindJobsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { userData = {}, savedJobs = [], saveJob, unsaveJob, jobApplications = [] } = useProfessional() || {};
  const { profileData = {} } = useProfessionalOnboarding() || {};

  // ── User Profile State ──────────────────────────────────────
  const [userProfile, setUserProfile] = useState(null);
  const [profileJobTitle, setProfileJobTitle] = useState('');
  const [profileSkills, setProfileSkills] = useState([]);
  const [userId, setUserId] = useState('default');

  // ── Jobs State ──────────────────────────────────────────────
  const [allJobs, setAllJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [dbSavedJobs, setDbSavedJobs] = useState([]);

  // ── UI State ────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [savingJobId, setSavingJobId] = useState(null);

  // ── Search & Filter State ───────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [profileFilterActive, setProfileFilterActive] = useState(true);
  const [filters, setFilters] = useState({
    salary: 'all',
    experience: 'all',
    remote: false,
    location: '',
    skills: [],
  });
  const [pendingFilters, setPendingFilters] = useState({ ...filters });

  // ── Job Alerts State ────────────────────────────────────────
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [jobAlerts, setJobAlerts] = useState([]);
  const [alertName, setAlertName] = useState('');

  // ── Animations ──────────────────────────────────────────────
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const searchDebounce = useRef(null);

  // ═══════════════════════════════════════════════════════════
  // INIT: Load user profile & token
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const init = async () => {
      try {
        // Get user token
        const token = await AsyncStorage.getItem('userToken');
        if (token) setUserId(token.slice(0, 16));

        // Load profile from cache
        const raw = await AsyncStorage.getItem('userProfile');
        let profile = raw ? JSON.parse(raw) : null;

        // Merge with context
        if (userData?.email || profileData?.email) {
          profile = { ...(profile || {}), ...userData, ...profileData };
        }

        if (profile) {
          setUserProfile(profile);
          const title = profile.job_title || profile.profession || profile.current_title || '';
          setProfileJobTitle(title);
          const skills = profile.skills || profile.required_skills || [];
          setProfileSkills(Array.isArray(skills) ? skills : []);
          if (title) setSearchQuery(title); // Auto-fill search with profile job title
        }
      } catch (e) {
        console.error('Profile init error:', e);
      }
    };
    init();
  }, []);

  // ═══════════════════════════════════════════════════════════
  // LOAD JOBS FROM DB
  // ═══════════════════════════════════════════════════════════
  const loadJobs = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setIsLoading(true);

      // Load cache first for instant display
      const cacheKey = 'professional_findjobs_all';
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) setAllJobs(parsed);
        }
      } catch {}

      // Use shared fetchAllJobs: internal JobEng posts (with company logos
      // joined from company_profiles) + external jobs from hiring-site
      // aggregators (Adzuna / Reed / TheMuse via server proxy). Each item
      // carries a `source` field so cards can render a badge.
      const { fetchAllJobs } = require('./jobsShared');
      const { merged } = await fetchAllJobs({ limit: 200 });
      const jobs = merged;
      console.log(`✅ FindJobs loaded: ${jobs.length} jobs (${jobs.filter(j=>j.source==='jobeng').length} JobEng, ${jobs.filter(j=>j.source==='external').length} external)`);

      const scored = scoreJobs(jobs);
      setAllJobs(scored);

      try {
        if (scored.length > 0) {
          await AsyncStorage.setItem(cacheKey, JSON.stringify(scored));
        }
      } catch {}

      await loadAppliedAndSaved();
    } catch (err) {
      console.error('loadJobs error:', err);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [profileJobTitle, profileSkills]);

  const loadAppliedAndSaved = async () => {
    try {
      const email = userProfile?.email || userData?.email;
      if (email) {
        const apps = await apiFetch(
          `/api/data/applications?candidate_email=${encodeURIComponent(email)}&limit=100`
        );
        if (apps && !apps.error && Array.isArray(apps)) {
          setAppliedJobIds(new Set(apps.map(a => String(a.job_id || a.jobId || ''))));
        }
      }

      // Saved jobs from DB
      const svd = await apiFetch(`/api/data/saved_jobs?limit=100`);
      if (svd && !svd.error && Array.isArray(svd)) {
        setDbSavedJobs(svd);
        setSavedJobIds(new Set(svd.map(j => String(j.job_id || j.id || ''))));
      } else {
        // Fallback to context saved jobs
        if (Array.isArray(savedJobs)) {
          setSavedJobIds(new Set(savedJobs.map(j => String(j.id || ''))));
        }
      }

      // Load job alerts
      const alerts = await AsyncStorage.getItem(`professional_${userId}_jobalerts`);
      if (alerts) setJobAlerts(JSON.parse(alerts));
    } catch (e) {
      console.error('loadAppliedAndSaved error:', e);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // SCORE JOBS vs PROFILE
  // ═══════════════════════════════════════════════════════════
  const scoreJobs = (jobs) => {
    if (!profileJobTitle && profileSkills.length === 0) return jobs;

    return jobs.map(job => {
      let score = 0;
      const titleLower = (job.title || '').toLowerCase();
      const descLower = (job.description || '').toLowerCase();
      const jobSkills = job.required_skills || job.skills || [];

      // Title match
      if (profileJobTitle) {
        const terms = profileJobTitle.toLowerCase().split(' ');
        const matches = terms.filter(t => t.length > 2 && titleLower.includes(t));
        score += (matches.length / Math.max(terms.length, 1)) * 50;
      }

      // Skills match
      if (profileSkills.length > 0) {
        const matchedSkills = profileSkills.filter(skill => {
          const s = typeof skill === 'string' ? skill : (skill?.name || skill?.skill || '');
          if (!s) return false;
          const sLower = s.toLowerCase();
          return jobSkills.some(js => {
            const j = typeof js === 'string' ? js : (js?.name || js?.skill || '');
            return j && j.toLowerCase().includes(sLower);
          }) || descLower.includes(sLower);
        });
        score += (matchedSkills.length / Math.max(profileSkills.length, 1)) * 40;
      }

      // Recency bonus
      const created = new Date(job.created_at || 0);
      const daysSince = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) score += 10;
      else if (daysSince < 30) score += 5;

      return { ...job, matchScore: Math.min(Math.round(score), 100) };
    });
  };

  // ═══════════════════════════════════════════════════════════
  // FILTER & SORT JOBS
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    applyFiltersAndSort();
  }, [allJobs, searchQuery, selectedCategory, sortBy, filters, profileFilterActive, profileJobTitle]);

  const applyFiltersAndSort = () => {
    let result = [...allJobs];

    // 1. Profile job title filter (smart match)
    if (profileFilterActive && profileJobTitle) {
      const terms = profileJobTitle.toLowerCase().split(' ').filter(t => t.length > 2);
      if (terms.length > 0) {
        result = result.filter(job => {
          const combined = `${job.title} ${job.description || ''}`.toLowerCase();
          return terms.some(t => combined.includes(t));
        });
      }
    }

    // 2. Search query filter (always apply when present)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(job =>
        (job.title || '').toLowerCase().includes(q) ||
        (job.company || '').toLowerCase().includes(q) ||
        (job.location || '').toLowerCase().includes(q) ||
        (job.description || '').toLowerCase().includes(q) ||
        (job.department || '').toLowerCase().includes(q) ||
        (job.required_skills || []).some(s => s.toLowerCase().includes(q))
      );
    }

    // 3. Category filter
    const cat = JOB_CATEGORIES.find(c => c.id === selectedCategory);
    if (cat && cat.filter) {
      if (cat.filter === 'remote') {
        result = result.filter(j => j.is_remote || j.remote);
      } else {
        result = result.filter(j =>
          (j.job_type || j.type || '').toLowerCase().includes(cat.filter)
        );
      }
    }

    // 4. Salary filter
    if (filters.salary !== 'all') {
      const range = SALARY_RANGES.find(r => r.id === filters.salary);
      if (range) {
        result = result.filter(j => {
          const sal = j.salary_max || j.salary_min || parseSalary(j.salary);
          return sal >= range.min && sal <= range.max;
        });
      }
    }

    // 5. Experience filter
    if (filters.experience !== 'all') {
      result = result.filter(j => {
        const exp = (j.experience_level || j.seniority || '').toLowerCase();
        return exp.includes(filters.experience);
      });
    }

    // 6. Remote filter
    if (filters.remote) {
      result = result.filter(j => j.is_remote || j.remote);
    }

    // 7. Location filter
    if (filters.location.trim()) {
      const loc = String(filters.location ?? '').toLowerCase();
      result = result.filter(j => (j.location || '').toLowerCase().includes(loc));
    }

    // 8. Skills filter
    if (filters.skills.length > 0) {
      result = result.filter(job =>
        filters.skills.every(skill =>
          (job.required_skills || []).some(js => js.toLowerCase().includes(skill.toLowerCase())) ||
          (job.description || '').toLowerCase().includes(skill.toLowerCase())
        )
      );
    }

    // 9. Sort
    switch (sortBy) {
      case 'date':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'salary_desc':
        result.sort((a, b) => (parseSalary(b.salary) || b.salary_max || 0) - (parseSalary(a.salary) || a.salary_max || 0));
        break;
      case 'salary_asc':
        result.sort((a, b) => (parseSalary(a.salary) || a.salary_min || 0) - (parseSalary(b.salary) || b.salary_min || 0));
        break;
      default: // relevance
        result.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
    }

    setFilteredJobs(result);
  };

  const parseSalary = (salStr) => {
    if (!salStr) return 0;
    const nums = String(salStr).replace(/[£,]/g, '').match(/\d+/g);
    if (!nums) return 0;
    return nums.length > 1
      ? (parseInt(nums[0]) + parseInt(nums[nums.length - 1])) / 2
      : parseInt(nums[0]);
  };

  // ═══════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════
  // Load on mount — don't wait for userId or profileJobTitle
  // profileJobTitle filter is applied client-side in applyFiltersAndSort
  useEffect(() => {
    loadJobs(true);
    Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }).start();
  }, []);

  // When profileJobTitle resolves, update search box + re-filter (client-side only)
  const prevTitleRef = React.useRef('');
  useEffect(() => {
    if (!profileJobTitle || profileJobTitle === prevTitleRef.current) return;
    prevTitleRef.current = profileJobTitle;
    setSearchQuery(profileJobTitle);
    // applyFiltersAndSort runs automatically via the allJobs/searchQuery useEffect
  }, [profileJobTitle]);

  const onRefresh = () => {
    setRefreshing(true);
    loadJobs(false);
  };

  // ═══════════════════════════════════════════════════════════
  // APPLY TO JOB
  // ═══════════════════════════════════════════════════════════
  const handleApply = async (job) => {
    if (appliedJobIds.has(String(job.id))) {
      Alert.alert('Already Applied', 'You have already applied to this position.');
      return;
    }

    // External jobs (Adzuna/Reed/Indeed) → open the hiring site in browser.
    // We can't apply to someone else's job from inside the app.
    if (job.source === 'external' && job.external_url) {
      Alert.alert(
        `Apply on ${job.source_name || 'hiring site'}`,
        `This job is posted on ${job.source_name || 'an external site'}. You'll be redirected there to complete your application.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Open',
            onPress: async () => {
              try {
                const { Linking } = require('react-native');
                await Linking.openURL(job.external_url);
                // Still log the click so we can analytics the funnel
                apiFetch('/api/data/external_clicks', {
                  method: 'POST',
                  body: JSON.stringify({
                    job_id: job.id,
                    source_name: job.source_name || 'unknown',
                    url: job.external_url,
                    clicked_at: new Date().toISOString(),
                  }),
                }).catch(() => {});
              } catch { Alert.alert('Error', 'Could not open the hiring site.'); }
            },
          },
        ]
      );
      return;
    }

    Alert.alert(
      'Apply to Job',
      `Apply for ${job.title}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Quick Apply',
          onPress: () => quickApply(job),
        },
        {
          text: 'Full Application',
          onPress: () => navigation.navigate('SubmitForJobs', { jobId: job.id, job }),
        },
      ]
    );
  };

  const quickApply = async (job) => {
    try {
      setApplyingJobId(job.id);

      // ── Profile loading: try multiple sources since userProfile may not be set ──
      let profile = userProfile || userData || {};
      if (!profile.email) {
        try {
          const profRaw = await AsyncStorage.getItem('prof_user_profile');
          if (profRaw) {
            const p = JSON.parse(profRaw);
            profile = { ...p.personal, ...p, ...profile };
          }
        } catch {}
      }
      if (!profile.email) {
        try {
          const authRaw = await AsyncStorage.getItem('userData') || await AsyncStorage.getItem('user');
          if (authRaw) {
            const a = JSON.parse(authRaw);
            profile = { ...a.user, ...a, ...profile };
          }
        } catch {}
      }

      const payload = {
        job_id: job.id,
        candidate_name: profile.full_name || profile.name || 'Applicant',
        candidate_email: (profile.email || '').toLowerCase(),
        candidate_phone: profile.phone || '',
        candidate_location: profile.location || profile.address || 'UK',
        current_title: profile.job_title || profile.profession || '',
        experience_years: profile.experience_years || 0,
        skills: profileSkills,
        source: 'FindJobsScreen_QuickApply',
        applied_date: new Date().toISOString(),
        status: 'applied',
      };

      console.log('📮 quickApply payload:', JSON.stringify({
        job_id: payload.job_id,
        candidate_email: payload.candidate_email,
        candidate_name: payload.candidate_name,
        source: payload.source,
      }));

      if (!payload.candidate_email) {
        Alert.alert('Email required', 'Please complete your profile email before applying.');
        return;
      }
      if (!payload.job_id) {
        Alert.alert('Error', 'Job reference missing. Please try again.');
        return;
      }

      const result = await apiFetch('/api/recruitment/apply', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.log('📨 quickApply response:', JSON.stringify(result).slice(0, 300));

      if (result && result.success && !result.error) {
        setAppliedJobIds(prev => new Set([...prev, String(job.id)]));
        Alert.alert('✅ Applied!', `Your application for ${job.title} has been submitted.`);
      } else {
        Alert.alert('Apply failed', result?.message || 'The server rejected the application. Tap "Full Application" to try the detailed form.');
      }
    } catch (err) {
      console.log('❌ quickApply crash:', err.message);
      navigation.navigate('SubmitForJobs', { jobId: job.id, job });
    } finally {
      setApplyingJobId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // SAVE JOB TO DB
  // ═══════════════════════════════════════════════════════════
  const handleSaveJob = async (job) => {
    const jobIdStr = String(job.id);
    const isSaved = savedJobIds.has(jobIdStr);
    setSavingJobId(job.id);

    try {
      if (isSaved) {
        // Remove saved
        const savedEntry = dbSavedJobs.find(s => String(s.job_id) === jobIdStr);
        if (savedEntry?.id) {
          await apiFetch(`/api/data/saved_jobs/${savedEntry.id}`, { method: 'DELETE' });
        }
        setSavedJobIds(prev => { const n = new Set(prev); n.delete(jobIdStr); return n; });
        setDbSavedJobs(prev => prev.filter(s => String(s.job_id) !== jobIdStr));
        if (unsaveJob) unsaveJob(job.id);
      } else {
        // Save job to DB
        const payload = {
          job_id: job.id,
          job_title: job.title,
          company: job.company,
          location: job.location,
          salary: job.salary,
          job_type: job.job_type || job.type,
          saved_at: new Date().toISOString(),
        };
        const result = await apiFetch('/api/data/saved_jobs', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setSavedJobIds(prev => new Set([...prev, jobIdStr]));
        if (result && !result.error) {
          setDbSavedJobs(prev => [...prev, { ...payload, id: result.id }]);
        }
        if (saveJob) saveJob(job);
      }
    } catch (err) {
      Alert.alert('Error', 'Failed to save job. Please try again.');
    } finally {
      setSavingJobId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // JOB ALERTS
  // ═══════════════════════════════════════════════════════════
  const createJobAlert = async () => {
    if (!alertName.trim()) {
      Alert.alert('Error', 'Please enter an alert name.');
      return;
    }
    const alert = {
      id: Date.now().toString(),
      name: alertName.trim(),
      query: searchQuery,
      filters: { ...filters, category: selectedCategory },
      createdAt: new Date().toISOString(),
      jobCount: filteredJobs.length,
    };
    const updated = [...jobAlerts, alert];
    setJobAlerts(updated);
    await AsyncStorage.setItem(`professional_${userId}_jobalerts`, JSON.stringify(updated));
    setAlertName('');
    setShowAlertModal(false);
    Alert.alert('✅ Alert Created', `You'll be notified of new jobs matching "${alert.name}".`);
  };

  const deleteJobAlert = async (alertId) => {
    const updated = jobAlerts.filter(a => a.id !== alertId);
    setJobAlerts(updated);
    await AsyncStorage.setItem(`professional_${userId}_jobalerts`, JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════════
  // SEARCH DEBOUNCE
  // ═══════════════════════════════════════════════════════════
  const handleSearch = (text) => {
    setSearchQuery(text);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      // If user clears to empty, re-apply profile title filter
      if (!text.trim() && profileJobTitle) {
        setProfileFilterActive(true);
      }
    }, 300);
  };

  // ═══════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════
  const formatSalary = (job) => {
    if (job.salary_min && job.salary_max)
      return `£${Math.round(job.salary_min/1000)}k – £${Math.round(job.salary_max/1000)}k`;
    if (job.salary) return job.salary;
    return 'Competitive';
  };

  const formatPosted = (dateStr) => {
    if (!dateStr) return 'Recently';
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days/7)}w ago`;
    return `${Math.floor(days/30)}mo ago`;
  };

  const isApplied = (jobId) => appliedJobIds.has(String(jobId));
  const isSaved = (jobId) => savedJobIds.has(String(jobId));

  const getMatchColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#3b82f6';
    return '#94a3b8';
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: HEADER
  // ═══════════════════════════════════════════════════════════
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>Find Jobs</Text>
        <Text style={styles.headerSub}>{filteredJobs.length} opportunities</Text>
      </View>
      <View style={styles.headerRight}>
        <TouchableOpacity
          style={styles.headerBtn}
          onPress={() => setShowAlertModal(true)}
        >
          <Ionicons name="notifications-outline" size={20} color="#fff" />
          {jobAlerts.length > 0 && (
            <View style={styles.alertBadge}>
              <Text style={styles.alertBadgeText}>{jobAlerts.length}</Text>
            </View>
          )}
        </TouchableOpacity>
        <TouchableOpacity style={styles.headerBtn} onPress={() => setShowFilters(true)}>
          <Ionicons name="options-outline" size={20} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: PROFILE MATCH BANNER
  // ═══════════════════════════════════════════════════════════
  const renderProfileBanner = () => {
    if (!profileJobTitle) return null;
    return (
      <View style={styles.profileBanner}>
        <View style={styles.profileBannerLeft}>
          <MaterialCommunityIcons name="account-check" size={20} color="#0e4e96" />
          <View style={{ marginLeft: 10 }}>
            <Text style={styles.profileBannerTitle}>Matched to your profile</Text>
            <Text style={styles.profileBannerSub}>
              Showing jobs for: <Text style={{ fontWeight: '700' }}>{profileJobTitle}</Text>
            </Text>
          </View>
        </View>
        <Switch
          value={profileFilterActive}
          onValueChange={setProfileFilterActive}
          trackColor={{ false: '#cbd5e1', true: '#bfdbfe' }}
          thumbColor={profileFilterActive ? '#0e4e96' : '#94a3b8'}
        />
      </View>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: SEARCH
  // ═══════════════════════════════════════════════════════════
  const renderSearch = () => (
    <View style={styles.searchSection}>
      <View style={styles.searchBox}>
        <Ionicons name="search" size={18} color="#64748b" />
        <TextInput
          style={styles.searchInput}
          placeholder="Job title, company, or skills..."
          value={searchQuery}
          onChangeText={handleSearch}
          placeholderTextColor="#94a3b8"
          returnKeyType="search"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => { setSearchQuery(''); setProfileFilterActive(true); }}>
            <Ionicons name="close-circle" size={18} color="#94a3b8" />
          </TouchableOpacity>
        )}
      </View>
      <TouchableOpacity style={styles.sortBtn} onPress={() => setShowSort(true)}>
        <MaterialCommunityIcons name="sort" size={18} color="#0e4e96" />
        <Text style={styles.sortBtnText}>{SORT_OPTIONS.find(s => s.id === sortBy)?.label || 'Sort'}</Text>
      </TouchableOpacity>
    </View>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: CATEGORIES
  // ═══════════════════════════════════════════════════════════
  const renderCategories = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoriesRow} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {JOB_CATEGORIES.map(cat => {
        const count = cat.id === 'all'
          ? allJobs.length
          : allJobs.filter(j => {
              if (cat.filter === 'remote') return j.is_remote || j.remote;
              return (j.job_type || j.type || '').toLowerCase().includes(cat.filter);
            }).length;
        const active = selectedCategory === cat.id;
        return (
          <TouchableOpacity
            key={cat.id}
            style={[styles.catChip, active && { backgroundColor: cat.color, borderColor: cat.color }]}
            onPress={() => setSelectedCategory(cat.id)}
          >
            <Ionicons name={cat.icon} size={14} color={active ? '#fff' : cat.color} />
            <Text style={[styles.catChipText, active && { color: '#fff' }]}>{cat.title}</Text>
            <View style={[styles.catCount, { backgroundColor: active ? 'rgba(255,255,255,0.25)' : cat.color + '20' }]}>
              <Text style={[styles.catCountText, { color: active ? '#fff' : cat.color }]}>{count}</Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: STATS BAR
  // ═══════════════════════════════════════════════════════════
  const renderStats = () => {
    const high = filteredJobs.filter(j => (j.matchScore || 0) >= 80).length;
    const remote = filteredJobs.filter(j => j.is_remote || j.remote).length;
    const fresh = filteredJobs.filter(j => {
      const d = Math.floor((Date.now() - new Date(j.created_at || 0).getTime()) / 86400000);
      return d <= 3;
    }).length;
    return (
      <View style={styles.statsBar}>
        {[
          { label: 'Total', val: filteredJobs.length, icon: 'briefcase-outline', color: '#667eea' },
          { label: 'Best Match', val: high, icon: 'star-outline', color: '#10b981' },
          { label: 'Remote', val: remote, icon: 'home-outline', color: '#4ecdc4' },
          { label: 'New (3d)', val: fresh, icon: 'time-outline', color: '#f59e0b' },
        ].map((s, i) => (
          <View key={i} style={styles.statItem}>
            <Ionicons name={s.icon} size={16} color={s.color} />
            <Text style={[styles.statVal, { color: s.color }]}>{s.val}</Text>
            <Text style={styles.statLabel}>{s.label}</Text>
          </View>
        ))}
      </View>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: JOB CARD
  // ═══════════════════════════════════════════════════════════
  const renderJobCard = ({ item: job }) => {
    const saved = isSaved(job.id);
    const applied = isApplied(job.id);
    const applying = applyingJobId === job.id;
    const saving = savingJobId === job.id;
    const match = job.matchScore || 0;
    const skills = job.required_skills || job.skills || [];

    return (
      <Animated.View style={[styles.jobCard, { opacity: fadeAnim }]}>
        {/* Badges row */}
        <View style={styles.badgeRow}>
          {match >= 80 && (
            <View style={[styles.badge, { backgroundColor: '#dcfce7' }]}>
              <Ionicons name="star" size={10} color="#16a34a" />
              <Text style={[styles.badgeText, { color: '#16a34a' }]}>Top Match</Text>
            </View>
          )}
          {(job.is_urgent || job.urgent) && (
            <View style={[styles.badge, { backgroundColor: '#fee2e2' }]}>
              <Ionicons name="flash" size={10} color="#dc2626" />
              <Text style={[styles.badgeText, { color: '#dc2626' }]}>Urgent</Text>
            </View>
          )}
          {(job.is_remote || job.remote) && (
            <View style={[styles.badge, { backgroundColor: '#d1fae5' }]}>
              <Ionicons name="home" size={10} color="#059669" />
              <Text style={[styles.badgeText, { color: '#059669' }]}>Remote</Text>
            </View>
          )}
          {(job.is_featured || job.featured) && (
            <View style={[styles.badge, { backgroundColor: '#fef3c7' }]}>
              <Ionicons name="ribbon" size={10} color="#d97706" />
              <Text style={[styles.badgeText, { color: '#d97706' }]}>Featured</Text>
            </View>
          )}
        </View>

        {/* Header row: logo + info + save */}
        <View style={styles.cardHeader}>
          <View style={styles.logoBox}>
            {(() => {
              const { resolveLogo } = require('./jobsShared');
              const logoUri = resolveLogo(job);
              return logoUri ? (
                <Image
                  source={{ uri: logoUri }}
                  style={styles.companyLogo}
                  onError={() => {}}
                  defaultSource={undefined}
                />
              ) : (
                <View style={[styles.logoPlaceholder, { backgroundColor: '#0e4e96' }]}>
                  <Text style={styles.logoInitial}>
                    {(job.company || job.company_name || job.department || 'J').charAt(0).toUpperCase()}
                  </Text>
                </View>
              );
            })()}
          </View>

          <View style={styles.cardInfo}>
            <View style={styles.titleRow}>
              <Text style={styles.jobTitle} numberOfLines={2}>{job.title || 'Position'}</Text>
              {job.source === 'external' && (
                <View style={styles.externalBadge}>
                  <Ionicons name="globe-outline" size={10} color="#6366f1" />
                  <Text style={styles.externalBadgeText} numberOfLines={1}>{job.source_name || 'External'}</Text>
                </View>
              )}
              {job.source === 'jobeng' && (
                <View style={styles.jobengBadge}>
                  <Ionicons name="shield-checkmark" size={10} color="#059669" />
                  <Text style={styles.jobengBadgeText}>JobEng</Text>
                </View>
              )}
            </View>
            <Text style={styles.companyText}>{job.company || job.company_name || job.department || 'Company'}</Text>
            <View style={styles.metaRow}>
              {job.location && (
                <View style={styles.metaItem}>
                  <Ionicons name="location-outline" size={11} color="#64748b" />
                  <Text style={styles.metaText} numberOfLines={1}>{job.location}</Text>
                </View>
              )}
              <View style={styles.metaItem}>
                <Ionicons name="cash-outline" size={11} color="#64748b" />
                <Text style={styles.metaText}>{formatSalary(job)}</Text>
              </View>
            </View>
          </View>

          <TouchableOpacity style={styles.saveBtn} onPress={() => handleSaveJob(job)} disabled={saving}>
            {saving ? (
              <ActivityIndicator size={16} color="#f59e0b" />
            ) : (
              <Ionicons name={saved ? 'bookmark' : 'bookmark-outline'} size={22} color={saved ? '#f59e0b' : '#94a3b8'} />
            )}
          </TouchableOpacity>
        </View>

        {/* Match score bar */}
        {match > 0 && (
          <View style={styles.matchRow}>
            <View style={styles.matchBarBg}>
              <View style={[styles.matchBarFill, { width: `${match}%`, backgroundColor: getMatchColor(match) }]} />
            </View>
            <Text style={[styles.matchPct, { color: getMatchColor(match) }]}>{match}% match</Text>
          </View>
        )}

        {/* Description */}
        {job.description && (
          <Text style={styles.descText} numberOfLines={2}>{job.description}</Text>
        )}

        {/* Skills chips */}
        {skills.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.skillsRow}>
            {skills.slice(0, 5).map((skill, i) => {
              const skillStr = typeof skill === 'string' ? skill : (skill?.name || skill?.skill || '');
              if (!skillStr) return null;
              const skillLower = skillStr.toLowerCase();
              const isMySkill = profileSkills.some(s => {
                const ss = typeof s === 'string' ? s : (s?.name || s?.skill || '');
                return ss && ss.toLowerCase() === skillLower;
              });
              return (
                <View key={i} style={[styles.skillChip, isMySkill && styles.skillChipMatch]}>
                  {isMySkill && <Ionicons name="checkmark" size={10} color="#16a34a" />}
                  <Text style={[styles.skillChipText, isMySkill && styles.skillChipTextMatch]}>{skillStr}</Text>
                </View>
              );
            })}
            {skills.length > 5 && <Text style={styles.moreSkills}>+{skills.length - 5}</Text>}
          </ScrollView>
        )}

        {/* Footer: posted + actions */}
        <View style={styles.cardFooter}>
          <View style={styles.postedRow}>
            <Ionicons name="time-outline" size={12} color="#94a3b8" />
            <Text style={styles.postedText}>{formatPosted(job.created_at || job.posted_date)}</Text>
            {job.job_type && (
              <View style={styles.typeChip}>
                <Text style={styles.typeChipText}>{job.job_type}</Text>
              </View>
            )}
          </View>

          <View style={styles.actionRow}>
            <TouchableOpacity
              style={styles.detailsBtn}
              onPress={() => navigation.navigate('JobDetails', { jobId: job.id, job })}
            >
              <Text style={styles.detailsBtnText}>Details</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.applyBtn, applied && styles.appliedBtn, applying && styles.applyingBtn]}
              onPress={() => handleApply(job)}
              disabled={applied || applying}
            >
              {applying ? (
                <ActivityIndicator size={14} color="#fff" />
              ) : (
                <>
                  <Ionicons name={applied ? 'checkmark' : 'send-outline'} size={14} color="#fff" />
                  <Text style={styles.applyBtnText}>{applied ? 'Applied' : 'Apply'}</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER: EMPTY STATE
  // ═══════════════════════════════════════════════════════════
  const renderEmpty = () => (
    <View style={styles.emptyBox}>
      <MaterialCommunityIcons name="briefcase-search-outline" size={64} color="#cbd5e1" />
      <Text style={styles.emptyTitle}>No jobs found</Text>
      <Text style={styles.emptyText}>
        {profileFilterActive && profileJobTitle
          ? `No jobs match "${profileJobTitle}". Try disabling profile filter or change your search.`
          : 'Try adjusting your filters or search terms.'}
      </Text>
      <TouchableOpacity style={styles.resetBtn} onPress={() => {
        setSearchQuery(profileJobTitle);
        setSelectedCategory('all');
        setFilters({ salary: 'all', experience: 'all', remote: false, location: '', skills: [] });
        setProfileFilterActive(true);
        onRefresh();
      }}>
        <Ionicons name="refresh" size={16} color="#0e4e96" />
        <Text style={styles.resetBtnText}>Reset & Refresh</Text>
      </TouchableOpacity>
    </View>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: FILTER MODAL
  // ═══════════════════════════════════════════════════════════
  const renderFilterModal = () => (
    <Modal visible={showFilters} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalWrap}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Filters</Text>
          <TouchableOpacity onPress={() => {
            setPendingFilters({ salary: 'all', experience: 'all', remote: false, location: '', skills: [] });
          }}>
            <Text style={styles.clearText}>Clear All</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowFilters(false)}>
            <Ionicons name="close" size={24} color="#1e293b" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          {/* Salary */}
          <Text style={styles.filterLabel}>Salary Range</Text>
          <View style={styles.chipGroup}>
            {SALARY_RANGES.map(r => (
              <TouchableOpacity
                key={r.id}
                style={[styles.filterChip, pendingFilters.salary === r.id && styles.filterChipActive]}
                onPress={() => setPendingFilters(p => ({ ...p, salary: r.id }))}
              >
                <Text style={[styles.filterChipText, pendingFilters.salary === r.id && styles.filterChipTextActive]}>
                  {r.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Experience */}
          <Text style={styles.filterLabel}>Experience Level</Text>
          <View style={styles.chipGroup}>
            {EXPERIENCE_LEVELS.map(e => (
              <TouchableOpacity
                key={e.id}
                style={[styles.filterChip, pendingFilters.experience === e.id && styles.filterChipActive]}
                onPress={() => setPendingFilters(p => ({ ...p, experience: e.id }))}
              >
                <Text style={[styles.filterChipText, pendingFilters.experience === e.id && styles.filterChipTextActive]}>
                  {e.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Location */}
          <Text style={styles.filterLabel}>Location</Text>
          <TextInput
            style={styles.filterInput}
            placeholder="e.g. London, Manchester..."
            value={pendingFilters.location}
            onChangeText={t => setPendingFilters(p => ({ ...p, location: t }))}
            placeholderTextColor="#94a3b8"
          />

          {/* Skills from profile */}
          {profileSkills.length > 0 && (
            <>
              <Text style={styles.filterLabel}>Filter by Your Skills</Text>
              <View style={styles.chipGroup}>
                {profileSkills.slice(0, 10).map((skill, i) => {
                  const skillStr = typeof skill === 'string' ? skill : (skill?.name || skill?.skill || '');
                  if (!skillStr) return null;
                  const active = pendingFilters.skills.includes(skillStr);
                  return (
                    <TouchableOpacity
                      key={i}
                      style={[styles.filterChip, active && styles.filterChipActive]}
                      onPress={() => setPendingFilters(p => ({
                        ...p,
                        skills: active ? p.skills.filter(s => s !== skillStr) : [...p.skills, skillStr],
                      }))}
                    >
                      {active && <Ionicons name="checkmark" size={12} color="#fff" />}
                      <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>{skillStr}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </>
          )}

          {/* Remote toggle */}
          <View style={styles.toggleRow}>
            <Text style={styles.filterLabel}>Remote Only</Text>
            <Switch
              value={pendingFilters.remote}
              onValueChange={v => setPendingFilters(p => ({ ...p, remote: v }))}
              trackColor={{ false: '#e2e8f0', true: '#bfdbfe' }}
              thumbColor={pendingFilters.remote ? '#0e4e96' : '#94a3b8'}
            />
          </View>
        </ScrollView>

        <TouchableOpacity
          style={styles.applyFiltersBtn}
          onPress={() => { setFilters({ ...pendingFilters }); setShowFilters(false); }}
        >
          <Text style={styles.applyFiltersBtnText}>Apply Filters ({filteredJobs.length} jobs)</Text>
        </TouchableOpacity>
      </SafeAreaView>
    </Modal>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: SORT MODAL
  // ═══════════════════════════════════════════════════════════
  const renderSortModal = () => (
    <Modal visible={showSort} transparent animationType="fade">
      <TouchableOpacity style={styles.sortOverlay} onPress={() => setShowSort(false)}>
        <View style={styles.sortSheet}>
          <Text style={styles.sortTitle}>Sort By</Text>
          {SORT_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.id}
              style={styles.sortOption}
              onPress={() => { setSortBy(opt.id); setShowSort(false); }}
            >
              <Text style={[styles.sortOptionText, sortBy === opt.id && styles.sortOptionActive]}>
                {opt.label}
              </Text>
              {sortBy === opt.id && <Ionicons name="checkmark" size={18} color="#0e4e96" />}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER: JOB ALERTS MODAL
  // ═══════════════════════════════════════════════════════════
  const renderAlertModal = () => (
    <Modal visible={showAlertModal} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={styles.modalWrap}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Job Alerts</Text>
          <TouchableOpacity onPress={() => setShowAlertModal(false)}>
            <Ionicons name="close" size={24} color="#1e293b" />
          </TouchableOpacity>
        </View>
        <ScrollView style={styles.modalBody}>
          {/* Create new alert */}
          <View style={styles.alertCreateBox}>
            <Text style={styles.filterLabel}>Create Alert for Current Search</Text>
            <Text style={styles.alertDesc}>
              Query: "{searchQuery || 'All jobs'}" · {filteredJobs.length} current results
            </Text>
            <TextInput
              style={styles.filterInput}
              placeholder="Alert name (e.g. London React Jobs)..."
              value={alertName}
              onChangeText={setAlertName}
              placeholderTextColor="#94a3b8"
            />
            <TouchableOpacity style={styles.createAlertBtn} onPress={createJobAlert}>
              <Ionicons name="notifications" size={16} color="#fff" />
              <Text style={styles.createAlertBtnText}>Create Alert</Text>
            </TouchableOpacity>
          </View>

          {/* Existing alerts */}
          {jobAlerts.length > 0 && (
            <>
              <Text style={styles.filterLabel}>Your Alerts ({jobAlerts.length})</Text>
              {jobAlerts.map(alert => (
                <View key={alert.id} style={styles.alertCard}>
                  <View style={styles.alertInfo}>
                    <Text style={styles.alertName}>{alert.name}</Text>
                    <Text style={styles.alertMeta}>"{alert.query}" · {alert.jobCount} jobs</Text>
                    <Text style={styles.alertDate}>{new Date(alert.createdAt).toLocaleDateString('en-GB')}</Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => Alert.alert('Delete Alert', 'Remove this job alert?', [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'Delete', style: 'destructive', onPress: () => deleteJobAlert(alert.id) },
                    ])}
                  >
                    <Ionicons name="trash-outline" size={20} color="#ef4444" />
                  </TouchableOpacity>
                </View>
              ))}
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ═══════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════
  if (isLoading && allJobs.length === 0) {
    return (
      <SafeAreaView style={styles.safe}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        {renderHeader()}
        <View style={styles.loadingBox}>
          <ActivityIndicator size={40} color="#0e4e96" />
          <Text style={styles.loadingText}>Finding jobs for you...</Text>
          {profileJobTitle ? (
            <Text style={styles.loadingSubText}>Matching against: {profileJobTitle}</Text>
          ) : null}
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}

      <FlatList
        data={filteredJobs}
        keyExtractor={item => String(item.id || Math.random())}
        renderItem={renderJobCard}
        ListHeaderComponent={
          <>
            {renderProfileBanner()}
            {renderSearch()}
            {renderCategories()}
            {renderStats()}
          </>
        }
        ListEmptyComponent={!isLoading ? renderEmpty() : null}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} tintColor="#0e4e96" />
        }
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={10}
      />

      {renderFilterModal()}
      {renderSortModal()}
      {renderAlertModal()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════
const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0e4e96' },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#0e4e96' },
  backBtn: { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, marginHorizontal: 12 },
  headerTitle: { fontSize: 20, fontWeight: '700', color: '#fff' },
  headerSub: { fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 1 },
  headerRight: { flexDirection: 'row', gap: 8 },
  headerBtn: { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  alertBadge: { position: 'absolute', top: 4, right: 4, backgroundColor: '#ef4444', borderRadius: 8, minWidth: 14, height: 14, justifyContent: 'center', alignItems: 'center' },
  alertBadgeText: { color: '#fff', fontSize: 8, fontWeight: '700' },

  // Profile banner
  profileBanner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#eff6ff', borderBottomWidth: 1, borderBottomColor: '#bfdbfe', paddingHorizontal: 16, paddingVertical: 10 },
  profileBannerLeft: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  profileBannerTitle: { fontSize: 12, fontWeight: '600', color: '#1e40af' },
  profileBannerSub: { fontSize: 11, color: '#64748b', marginTop: 1 },

  // Search
  searchSection: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 10, gap: 8 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: '#e2e8f0', gap: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#1e293b', padding: 0 },
  sortBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, gap: 4, borderWidth: 1, borderColor: '#bfdbfe' },
  sortBtnText: { fontSize: 12, color: '#0e4e96', fontWeight: '600' },

  // Categories
  categoriesRow: { backgroundColor: '#fff', paddingVertical: 10 },
  catChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 20, paddingHorizontal: 10, paddingVertical: 6, marginRight: 8, gap: 5 },
  catChipText: { fontSize: 13, fontWeight: '600', color: '#475569' },
  catCount: { borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 },
  catCountText: { fontSize: 10, fontWeight: '700' },

  // Stats
  statsBar: { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#f1f5f9', paddingVertical: 12 },
  statItem: { flex: 1, alignItems: 'center', gap: 2 },
  statVal: { fontSize: 18, fontWeight: '800' },
  statLabel: { fontSize: 10, color: '#94a3b8', fontWeight: '500' },

  // List
  listContent: { backgroundColor: '#f8fafc', paddingBottom: 40 },

  // Job card
  jobCard: { backgroundColor: '#fff', borderRadius: 16, marginHorizontal: 16, marginTop: 12, padding: 16, borderWidth: 1, borderColor: '#e2e8f0', elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  badge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8, gap: 4 },
  badgeText: { fontSize: 10, fontWeight: '600' },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 10 },
  logoBox: { marginRight: 12 },
  companyLogo: { width: 48, height: 48, borderRadius: 12 },
  logoPlaceholder: { width: 48, height: 48, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  logoInitial: { fontSize: 20, fontWeight: '800', color: '#fff' },
  titleRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  externalBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: '#eef2ff', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2, borderWidth: 1, borderColor: '#c7d2fe', maxWidth: 90 },
  externalBadgeText: { fontSize: 9, fontWeight: '700', color: '#4f46e5', letterSpacing: 0.2 },
  jobengBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: '#ecfdf5', borderRadius: 10, paddingHorizontal: 7, paddingVertical: 2, borderWidth: 1, borderColor: '#a7f3d0' },
  jobengBadgeText: { fontSize: 9, fontWeight: '700', color: '#059669', letterSpacing: 0.2 },
  cardInfo: { flex: 1 },
  jobTitle: { fontSize: 15, fontWeight: '700', color: '#1e293b', lineHeight: 20, marginBottom: 3 },
  companyText: { fontSize: 13, fontWeight: '600', color: '#0e4e96', marginBottom: 5 },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  metaText: { fontSize: 11, color: '#64748b' },
  saveBtn: { width: 36, height: 36, justifyContent: 'center', alignItems: 'center' },

  // Match bar
  matchRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  matchBarBg: { flex: 1, height: 4, backgroundColor: '#f1f5f9', borderRadius: 2 },
  matchBarFill: { height: 4, borderRadius: 2 },
  matchPct: { fontSize: 12, fontWeight: '700', minWidth: 65 },

  // Description
  descText: { fontSize: 13, color: '#64748b', lineHeight: 18, marginBottom: 10 },

  // Skills
  skillsRow: { marginBottom: 12 },
  skillChip: { backgroundColor: '#f1f5f9', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4, marginRight: 6, flexDirection: 'row', alignItems: 'center', gap: 3 },
  skillChipMatch: { backgroundColor: '#dcfce7', borderWidth: 1, borderColor: '#86efac' },
  skillChipText: { fontSize: 11, color: '#475569', fontWeight: '500' },
  skillChipTextMatch: { color: '#15803d' },
  moreSkills: { fontSize: 11, color: '#94a3b8', alignSelf: 'center', marginLeft: 2 },

  // Footer
  cardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#f1f5f9', paddingTop: 10 },
  postedRow: { flexDirection: 'row', alignItems: 'center', gap: 5, flex: 1 },
  postedText: { fontSize: 11, color: '#94a3b8' },
  typeChip: { backgroundColor: '#f1f5f9', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 },
  typeChipText: { fontSize: 10, color: '#64748b', fontWeight: '500' },
  actionRow: { flexDirection: 'row', gap: 8 },
  detailsBtn: { backgroundColor: '#eff6ff', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 7, borderWidth: 1, borderColor: '#bfdbfe' },
  detailsBtnText: { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
  applyBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 7, gap: 5 },
  appliedBtn: { backgroundColor: '#10b981' },
  applyingBtn: { backgroundColor: '#94a3b8' },
  applyBtnText: { fontSize: 12, color: '#fff', fontWeight: '600' },

  // Loading
  loadingBox: { flex: 1, backgroundColor: '#f8fafc', justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 16, fontSize: 16, color: '#64748b', fontWeight: '500' },
  loadingSubText: { marginTop: 6, fontSize: 13, color: '#94a3b8' },

  // Empty
  emptyBox: { alignItems: 'center', paddingVertical: 60, paddingHorizontal: 32 },
  emptyTitle: { fontSize: 20, fontWeight: '700', color: '#1e293b', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#64748b', textAlign: 'center', lineHeight: 20, marginBottom: 24 },
  resetBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12, borderWidth: 1, borderColor: '#bfdbfe', gap: 8 },
  resetBtnText: { fontSize: 14, color: '#0e4e96', fontWeight: '600' },

  // Modal
  modalWrap: { flex: 1, backgroundColor: '#fff' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#1e293b', flex: 1 },
  clearText: { fontSize: 14, color: '#ef4444', fontWeight: '600', marginRight: 12 },
  modalBody: { flex: 1, paddingHorizontal: 20, paddingTop: 16 },
  filterLabel: { fontSize: 15, fontWeight: '700', color: '#1e293b', marginBottom: 10, marginTop: 16 },
  filterInput: { backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, fontSize: 14, color: '#1e293b', marginBottom: 8 },
  chipGroup: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  filterChip: { backgroundColor: '#f8fafc', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, flexDirection: 'row', alignItems: 'center', gap: 4 },
  filterChipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  filterChipText: { fontSize: 13, color: '#64748b', fontWeight: '500' },
  filterChipTextActive: { color: '#fff' },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8 },
  applyFiltersBtn: { backgroundColor: '#0e4e96', margin: 16, borderRadius: 14, paddingVertical: 16, alignItems: 'center' },
  applyFiltersBtnText: { fontSize: 16, color: '#fff', fontWeight: '700' },

  // Sort modal
  sortOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  sortSheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 40 },
  sortTitle: { fontSize: 17, fontWeight: '700', color: '#1e293b', marginBottom: 16 },
  sortOption: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  sortOptionText: { fontSize: 15, color: '#475569' },
  sortOptionActive: { color: '#0e4e96', fontWeight: '700' },

  // Alerts modal
  alertCreateBox: { backgroundColor: '#eff6ff', borderRadius: 14, padding: 16, marginBottom: 16 },
  alertDesc: { fontSize: 12, color: '#64748b', marginBottom: 10 },
  createAlertBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 12, justifyContent: 'center', gap: 8, marginTop: 8 },
  createAlertBtnText: { fontSize: 14, color: '#fff', fontWeight: '600' },
  alertCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 10, borderWidth: 1, borderColor: '#e2e8f0' },
  alertInfo: { flex: 1 },
  alertName: { fontSize: 14, fontWeight: '700', color: '#1e293b' },
  alertMeta: { fontSize: 12, color: '#64748b', marginTop: 2 },
  alertDate: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
});